import {get} from 'lodash';
import PropTypes from 'prop-types';
import imagePreviewData from 'components/imaging/ImagePreviewBuilder';
import './SelectCoverPageImage.css';

const GRE_COVER_SIZE = {
	width: 493,
	height: 523,
};

const AgentDetails = ({ agent }) => {
	const hasPhoto = get(agent, 'photo');
	return (
		<g transform={hasPhoto ? 'translate(115 0)' : ''}>
			{get(agent, 'name') && <text className="cover-agent name" x="52" y="692" fontFamily="Montserrat" fontSize="11" fontWeight="bold" textAnchor="start">{agent.name}</text>}
			{get(agent, 'tradeName') && <text className="cover-agent trade-name" x="52" y="706" fontFamily="Montserrat" fontSize="11" textAnchor="start">{agent.tradeName}</text>}
			{get(agent, 'address1') && <text className="cover-agent address1" x="52" y="727" fontFamily="Montserrat" fontSize="9" textAnchor="start">{agent.address1}</text>}
			{get(agent, 'address2') && <text className="cover-agent address2" x="52" y="741" fontFamily="Montserrat" fontSize="9" textAnchor="start">{agent.address2}</text>}
			{get(agent, 'mobilePhone') && <text className="cover-agent mobile-phone" x="52" y="759" fontFamily="Montserrat" fontSize="9" textAnchor="start">m: {agent.mobilePhone}</text>}
			{get(agent, 'workPhone') && <text className="cover-agent work-phone" x="52" y="773" fontFamily="Montserrat" fontSize="9" textAnchor="start">w: {agent.workPhone}</text>}
			{get(agent, 'workEmail') &&
				<foreignObject className="cover-agent work-email" x="52" y="787" width={hasPhoto ? 380 : 493} height="25"
							   textAnchor="start"><p style={{
					fontFamily: 'Montserrat', fontSize: 9, width: '100%', wordWrap: 'break-word',
				}}>{agent.workEmail}</p></foreignObject>}
		</g>
	);
};

const CoverPagePreview = ({address, agencyLogo, agent, featuredImage, featuredImageDragging = {}, width, height, preparedFor, preparedOn, resizeImageDimensions, showPageBorder, title, reportType, onCoverPageLoadError}) => {
	const coverImage = featuredImage?.src ? imagePreviewData({
		image: featuredImage,
		resizeDimensions: resizeImageDimensions,
		viewDimensions: GRE_COVER_SIZE,
	}) : null;

	const hasValidDimensions = coverImage && coverImage.view.width > 0 && coverImage.view.height > 0;
	const imageScaleX = hasValidDimensions ? GRE_COVER_SIZE.width / coverImage.view.width : 0;
	const imageScaleY = hasValidDimensions ? GRE_COVER_SIZE.height / coverImage.view.height : 0;

	const onError = (event) => {
		onCoverPageLoadError(event, reportType);
	};

	const hasAgentPhoto = get(agent, 'photo');

	return (
		<svg className="scaling-svg" id="large" width={width} height={height} viewBox="0 0 595 842">
			{showPageBorder && (<path id="page-border" stroke="#D8D8D8" strokeWidth="1" fill="#FFF" d="M0 0h595v842H0z"/>)}
			<text id="title" x="52" y="50" width="289" height="40" fontSize="16" fontFamily="Montserrat" fontWeight="600" dominantBaseline="middle" textAnchor="start">
				{title}
			</text>
			{agencyLogo && <image id="cover-agencylogo" x="341" y="30" width="204" height="40" href={agencyLogo} preserveAspectRatio="xMaxYMin"/>}
			{coverImage && (
					<foreignObject className="cover-primary-svg" x="52" y="91" width="493" height="523">
						<div style={{ position: 'relative', width: GRE_COVER_SIZE.width, height: GRE_COVER_SIZE.height, overflow: 'hidden' }}>
							<img className="cover-primary-image"
								 src={coverImage.image.uri}
								 alt=""
								 style={hasValidDimensions ? {
									 position: 'absolute',
									 width: Math.round(coverImage.image.width * imageScaleX),
									 height: Math.round(coverImage.image.height * imageScaleY),
									 left: Math.round(-coverImage.view.x * imageScaleX),
									 top: Math.round(-coverImage.view.y * imageScaleY),
								 } : {
									 width: '100%',
									 height: '100%',
									 objectFit: 'cover',
									 objectPosition: 'center',
								 }}
								 onMouseDown={featuredImageDragging.onMouseDown}
								 onMouseMove={featuredImageDragging.onMouseMove}
								 onMouseUp={featuredImageDragging.onMouseUp}
								 onMouseLeave={featuredImageDragging.onMouseUp}
								 onError={onError}
							/>
						</div>
					</foreignObject>
				)}
			{address && (
				<foreignObject x="51" y="640" width="493" height="20">
					<p id="address" style={{
						fontFamily: 'Montserrat', fontSize: 13, fontWeight: 'bold',
						height: 15, width: '100%', display: 'flex', alignItems: 'flex-end',
						margin: 0, padding: 0,
					}}>
						{address}
					</p>
				</foreignObject>
			)}
			{(preparedFor || preparedOn) && (
				<text id="prepared" x="51" y="666" width="493" fontSize="8" fontFamily="Montserrat">
					<tspan>Prepared</tspan>
					{preparedFor && <tspan> for <tspan fontWeight="bold">{preparedFor}</tspan></tspan>}
					{preparedOn && <tspan> on <tspan fontWeight="bold">{preparedOn}</tspan></tspan>}
				</text>
			)}
			{hasAgentPhoto && <image id="agent-photo" x="52" y="692" width="104" height="104" href={get(agent, 'photo')} preserveAspectRatio="xMidYMid"/>}
				<AgentDetails agent={agent} />
		</svg>
	);
};

AgentDetails.propTypes = {
	agent: PropTypes.shape({
		name: PropTypes.string,
		photo: PropTypes.string,
		tradeName: PropTypes.string,
		address1: PropTypes.string,
		address2: PropTypes.string,
		workPhone: PropTypes.string,
		mobilePhone: PropTypes.string,
		workEmail: PropTypes.string,
	}),
};

CoverPagePreview.propTypes = {
	address: PropTypes.string,
	agencyLogo: PropTypes.string,
	agent: PropTypes.shape({
		name: PropTypes.string,
		photo: PropTypes.string,
		tradeName: PropTypes.string,
		address1: PropTypes.string,
		address2: PropTypes.string,
		workPhone: PropTypes.string,
		mobilePhone: PropTypes.string,
		workEmail: PropTypes.string,
	}),
	featuredImage: PropTypes.shape({
		src: PropTypes.string,
		imageAspectRatio: PropTypes.string,
		x: PropTypes.number,
		y: PropTypes.number,
		zoom: PropTypes.number,
	}),
	featuredImageDragging: PropTypes.shape({
		onMouseDown: PropTypes.func,
		onMouseMove: PropTypes.func,
		onMouseUp: PropTypes.func,
	}),
	name: PropTypes.oneOf(['half', 'large']),
	width: PropTypes.number,
	height: PropTypes.number,
	preparedFor: PropTypes.string,
	preparedOn: PropTypes.string,
	resizeImageDimensions: PropTypes.shape({
		width: PropTypes.number,
		height: PropTypes.number,
	}),
	showPageBorder: PropTypes.bool,
	title: PropTypes.string,
	reportType: PropTypes.string,
	onCoverPageLoadError: PropTypes.func,
};

CoverPagePreview.defaultProps = {
	featuredImage: {},
	showPageBorder: true,
};

export default CoverPagePreview;
